import React from 'react';
import { Chip, createTheme, ThemeProvider } from '@mui/material';
import {CompactChipStyle, CustomStyleChip, DotChipStyle, LargeChipStyle } from './Styles';
import { POSITION_MODEL } from '../Helpers/PositionModel';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Chips for status
 * @param String background: Hexadecimal color for the background (required)
 * @param String fontColor: Hexadecimal color for the font (optional)
 * @param String legend: Text for the legend (required)
 * @param String compact: If variant=compact, all edges are rounded, else
 * @param String iconPosition: LEFT | RIGHT (from POSITION_MODEL)
 * @param {React.Component} icon - Icon component to display inside the chip
 * @param String classNameIcon: Classes for the icon
 * @param String borderColor - Hexadecimal color for the border (optional)
 * @param String className - Additional classes for the component (optional)
 * @param String variant - Variant style; default is 'compact'
 * @param String classNameIcon - CSS classes applied to the icon (optional)
 * @param {boolean} showDeleteIcon - Whether to display the delete icon on the chip. Hidden by default.
 * @param {...any} {...rest} - Any other valid props for MUI’s `Chip` component.
 * @returns {JSX.Element}
 */

const CustomChip = ({
    background,
    fontColor = '#FFFFFF',
    legend,
    borderColor,
    variant = 'compact',
    className = '',
    icon: IconComponent,
    classNameIcon,
    iconPosition = POSITION_MODEL.LEFT,
    deleteIcon,
    ...rest
}) => {
    const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim();
    const chipStyle = {
        ...CustomStyleChip,
        ...(variant === 'large' && LargeChipStyle),
        ...( (typeof legend == 'undefined' || legend === '') ? DotChipStyle : variant === 'compact' && CompactChipStyle),
    };

    const theme = createTheme({
        palette: {
            primary: {
                main: background || primaryColor,
                contrastText: fontColor,
            },
        },
        components: {
            MuiChip: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${borderColor ? borderColor : background} !important`,
                        boxSizing: 'border-box',
                    },
                },
            },
        },
    });
    // Normalizes the `icon` prop so it renders both as an element instance and as a
    // component type (SVGR icons are forwardRef objects, not functions).
    const renderIcon = (extraClassName = '') => {
        if (!IconComponent) return null;

        const iconClassName = `${classNameIcon ?? ''} ${extraClassName}`.trim() || undefined;

        if (React.isValidElement(IconComponent)) {
            const mergedClassName = `${IconComponent.props?.className ?? ''} ${iconClassName ?? ''}`.trim();
            return React.cloneElement(IconComponent, mergedClassName ? { className: mergedClassName } : {});
        }

        return <IconComponent className={iconClassName} />;
    };

    const renderRight = (
            <span className='d-flex align-items-center gap-4px'>
                {legend}
                {renderIcon('ms-1')}
            </span>
        );
        const deleteIconToRender = deleteIcon || undefined;
    return (
        <ThemeProvider theme={theme}>
            {iconPosition === POSITION_MODEL.LEFT ? (
                <Chip
                    className={className}
                    label={legend}
                    size='small'
                    color={'primary'}
                    sx={chipStyle}
                    icon={renderIcon()}
                    {...(deleteIconToRender ? { deleteIcon: deleteIconToRender } : {})}
                    {...rest}
                />
            ) : (
                <Chip size='small' color='primary' sx={chipStyle} className={className} label={renderRight} {...rest} />
            )}
        </ThemeProvider>
    );
};

export default CustomChip;
